import React, { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  validateMobileNumber,
  formatMobileNumber,
  sendOTP,
  verifyOTP,
  clearStoredOTP,
  validatePassword,
  validateName,
  formatCountdown,
} from '../utils/otpUtils';
import '../pages/SignupPage.css';

/**
 * Combined signup (same fields as SignupPage) + shipping address for guest checkout.
 * Shown on the Bag page; after success the shopper is signed in and navigated to Checkout.
 * On success, parent may navigate; user stays logged in via AuthContext.
 */
const CheckoutGuestAccountForm = ({ onSuccess }) => {
  const { signup, loading } = useAuth();

  const GOLD_TOAST_MS = 5000;
  const showGoldToast = useCallback((message) => {
    const text = typeof message === 'string' ? message.trim() : '';
    if (!text) return;
    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast(text, 'gold', GOLD_TOAST_MS);
    }
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    addressLine1: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'Pakistan',
  });
  const [errors, setErrors] = useState({});
  const [otpData, setOtpData] = useState({
    otp: '',
    isOtpSent: false,
    isOtpVerified: false,
    timer: 0,
    attempts: 0,
    maxAttempts: 3,
  });
  const [phoneVerificationToken, setPhoneVerificationToken] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      const formattedValue = formatMobileNumber(value);
      setFormData((prev) => ({ ...prev, [name]: formattedValue }));
      setPhoneVerificationToken(null);
      setOtpData((prev) => ({
        ...prev,
        otp: '',
        isOtpSent: false,
        isOtpVerified: false,
        timer: 0,
        attempts: 0,
      }));
      clearStoredOTP();
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleOtpChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtpData((prev) => ({ ...prev, otp: digitsOnly }));
    if (errors.otp) setErrors((prev) => ({ ...prev, otp: '' }));
  };

  const sendOtp = async () => {
    if (!formData.mobile.trim()) {
      const msg = 'Mobile number is required';
      setErrors((prev) => ({ ...prev, mobile: msg }));
      showGoldToast(msg);
      return;
    }
    if (!validateMobileNumber(formData.mobile)) {
      const msg = 'Please enter a valid Pakistani mobile number';
      setErrors((prev) => ({
        ...prev,
        mobile: msg,
      }));
      showGoldToast(msg);
      return;
    }
    try {
      const result = await sendOTP(formData.mobile);
      if (result.success) {
        setOtpData((prev) => ({
          ...prev,
          isOtpSent: true,
          timer: 60,
          attempts: 0,
        }));
        setPhoneVerificationToken(null);
        setErrors((prev) => ({ ...prev, mobile: '' }));
        const countdown = setInterval(() => {
          setOtpData((prev) => {
            if (prev.timer <= 1) {
              clearInterval(countdown);
              return { ...prev, timer: 0 };
            }
            return { ...prev, timer: prev.timer - 1 };
          });
        }, 1000);
      } else {
        const msg = result.message || 'Could not send WhatsApp code';
        setErrors((prev) => ({ ...prev, mobile: msg }));
        showGoldToast(msg);
      }
    } catch {
      const msg = 'Failed to send OTP. Please try again.';
      setErrors((prev) => ({ ...prev, mobile: msg }));
      showGoldToast(msg);
    }
  };

  const verifyOtp = async () => {
    if (!otpData.otp.trim()) {
      const msg = 'Please enter the OTP';
      setErrors((prev) => ({ ...prev, otp: msg }));
      showGoldToast(msg);
      return;
    }
    if (otpData.otp.length !== 6) {
      const msg = 'Please enter a 6-digit OTP';
      setErrors((prev) => ({ ...prev, otp: msg }));
      showGoldToast(msg);
      return;
    }
    try {
      const result = await verifyOTP(otpData.otp, formData.mobile);
      if (result.success) {
        setOtpData((prev) => ({ ...prev, isOtpVerified: true, attempts: 0 }));
        setPhoneVerificationToken(result.phoneVerificationToken || null);
        setErrors((prev) => ({
          ...prev,
          otp: '',
          otpSuccess: 'Mobile number verified successfully! ✓',
        }));
        setTimeout(() => {
          setErrors((prev) => ({ ...prev, otpSuccess: '' }));
        }, 3000);
      } else {
        setOtpData((prev) => ({ ...prev, attempts: prev.attempts + 1 }));
        setPhoneVerificationToken(null);
        const msg = result.message || 'Invalid code. Please try again.';
        setErrors((prev) => ({ ...prev, otp: msg }));
        showGoldToast(msg);
      }
    } catch {
      const msg = 'OTP verification failed. Please try again.';
      setErrors((prev) => ({ ...prev, otp: msg }));
      showGoldToast(msg);
    }
  };

  const resendOtp = async () => {
    if (otpData.attempts >= otpData.maxAttempts) {
      const msg = 'Maximum attempts reached. Please try again later.';
      setErrors((prev) => ({
        ...prev,
        mobile: msg,
      }));
      showGoldToast(msg);
      return;
    }
    try {
      const result = await sendOTP(formData.mobile);
      if (result.success) {
        setOtpData((prev) => ({
          ...prev,
          timer: 60,
          attempts: prev.attempts + 1,
        }));
        setPhoneVerificationToken(null);
        const countdown = setInterval(() => {
          setOtpData((prev) => {
            if (prev.timer <= 1) {
              clearInterval(countdown);
              return { ...prev, timer: 0 };
            }
            return { ...prev, timer: prev.timer - 1 };
          });
        }, 1000);
      } else {
        const msg = result.message || 'Could not resend WhatsApp code';
        setErrors((prev) => ({ ...prev, mobile: msg }));
        showGoldToast(msg);
      }
    } catch {
      const msg = 'Failed to resend OTP. Please try again.';
      setErrors((prev) => ({ ...prev, mobile: msg }));
      showGoldToast(msg);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const nameValidation = validateName(formData.name);
    if (!nameValidation.success) newErrors.name = nameValidation.message;

    if (!formData.mobile.trim()) newErrors.mobile = 'Mobile number is required';
    else if (!validateMobileNumber(formData.mobile))
      newErrors.mobile = 'Please enter a valid Pakistani mobile number';
    else if (!otpData.isOtpVerified)
      newErrors.mobile = 'Please verify your mobile number with the WhatsApp code';

    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';

    if (!formData.password.trim()) newErrors.password = 'Password is required';
    else {
      const pv = validatePassword(formData.password);
      if (!pv.success) newErrors.password = pv.message;
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'Street address is required';
    }
    if (!formData.city.trim()) newErrors.city = 'City is required';

    setErrors(newErrors);
    const errKeys = Object.keys(newErrors);
    if (errKeys.length === 0) return true;
    const order = [
      'general',
      'name',
      'mobile',
      'email',
      'password',
      'confirmPassword',
      'addressLine1',
      'city',
    ];
    const firstKey = order.find((k) => newErrors[k]) || errKeys[0];
    const firstMsg = newErrors[firstKey];
    if (firstMsg) showGoldToast(firstMsg);
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const phone = formData.mobile.trim();
    const shippingAddress = {
      addressLine1: formData.addressLine1.trim(),
      city: formData.city.trim(),
      state: (formData.state || '').trim(),
      postalCode: (formData.postalCode || '').trim(),
      country: (formData.country || 'Pakistan').trim(),
    };

    const result = await signup(formData.name, formData.email, phone, formData.password, {
      phoneVerificationToken: phoneVerificationToken || undefined,
      shippingAddress,
    });

    if (result.success) {
      if (typeof onSuccess === 'function') onSuccess();
    } else {
      if (result.fieldErrors) {
        const merged = { ...result.fieldErrors, general: result.error };
        setErrors(merged);
        const order = ['general', 'name', 'mobile', 'email', 'password', 'confirmPassword'];
        const firstKey = order.find((k) => merged[k]) || Object.keys(merged)[0];
        const firstMsg =
          (firstKey && merged[firstKey]) || result.error || 'Registration failed.';
        showGoldToast(typeof firstMsg === 'string' ? firstMsg : 'Registration failed.');
      } else {
        const msg = result.error || 'Registration failed.';
        setErrors({ general: msg });
        showGoldToast(msg);
      }
    }
  };

  return (
    <div className="checkout-guest-account auth-form-container">
      <div className="auth-form-header">
        <h2>Account &amp; delivery</h2>
        <p>Create your account and enter where we should deliver this order.</p>
      </div>

      <form onSubmit={handleSubmit} className="auth-form">
        {errors.general && (
          <div className="error-message general-error">{errors.general}</div>
        )}

        <div className="form-group">
          <label htmlFor="cg-name">Full name</label>
          <input
            type="text"
            id="cg-name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`input-field ${errors.name ? 'error' : ''}`}
            placeholder="Enter your full name"
            disabled={loading}
            autoComplete="name"
          />
          {errors.name && <span className="error-message">{errors.name}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="cg-mobile">Mobile number</label>
          <div className="mobile-input-group">
            <input
              type="tel"
              id="cg-mobile"
              name="mobile"
              value={formData.mobile}
              onChange={handleInputChange}
              className={`input-field mobile-input ${errors.mobile ? 'error' : ''} ${otpData.isOtpVerified ? 'verified' : ''}`}
              placeholder="03001234567 or +923001234567"
              disabled={loading || otpData.isOtpVerified}
              autoComplete="tel"
            />
            <button
              type="button"
              className={`otp-btn ${otpData.isOtpVerified ? 'verified' : ''}`}
              onClick={otpData.isOtpSent ? resendOtp : sendOtp}
              disabled={loading || !formData.mobile.trim() || otpData.timer > 0 || otpData.isOtpVerified}
            >
              {otpData.isOtpVerified
                ? '✓ Verified'
                : otpData.timer > 0
                  ? formatCountdown(otpData.timer)
                  : otpData.isOtpSent
                    ? 'Resend code'
                    : 'Send WhatsApp code'}
            </button>
          </div>
          {errors.mobile && <span className="error-message">{errors.mobile}</span>}
          {errors.otpSuccess && <span className="success-message">{errors.otpSuccess}</span>}
        </div>

        {otpData.isOtpSent && !otpData.isOtpVerified && (
          <div className="form-group otp-group">
            <label htmlFor="cg-otp">Enter 6-digit WhatsApp code</label>
            <div className="otp-input-group">
              <input
                type="text"
                id="cg-otp"
                value={otpData.otp}
                onChange={handleOtpChange}
                className={`input-field otp-input ${errors.otp ? 'error' : ''}`}
                placeholder="Code from WhatsApp"
                maxLength={6}
                disabled={loading}
                inputMode="numeric"
              />
              <button
                type="button"
                className="verify-btn"
                onClick={verifyOtp}
                disabled={
                  loading ||
                  !otpData.otp.trim() ||
                  otpData.otp.length !== 6 ||
                  otpData.attempts >= otpData.maxAttempts
                }
              >
                Verify
              </button>
            </div>
            {errors.otp && <span className="error-message">{errors.otp}</span>}
            <div className="otp-info">
              <small>
                Code sent on WhatsApp to <strong>{formData.mobile}</strong>.
              </small>
            </div>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="cg-email">Email</label>
          <input
            type="email"
            id="cg-email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={`input-field ${errors.email ? 'error' : ''}`}
            placeholder="Enter your email"
            disabled={loading}
            autoComplete="email"
          />
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="cg-password">Password</label>
          <div className="password-input-container">
            <input
              type={showPassword ? 'text' : 'password'}
              id="cg-password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={`input-field password-field ${errors.password ? 'error' : ''}`}
              placeholder="Create a password"
              disabled={loading}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword((v) => !v)}
              disabled={loading}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} eye-icon`} aria-hidden />
            </button>
          </div>
          {errors.password && <span className="error-message">{errors.password}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="cg-confirm">Confirm password</label>
          <div className="password-input-container">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="cg-confirm"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={`input-field password-field ${errors.confirmPassword ? 'error' : ''}`}
              placeholder="Confirm password"
              disabled={loading}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowConfirmPassword((v) => !v)}
              disabled={loading}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              <i
                className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} eye-icon`}
                aria-hidden
              />
            </button>
          </div>
          {errors.confirmPassword && (
            <span className="error-message">{errors.confirmPassword}</span>
          )}
        </div>

        <h3 className="checkout-guest-shipping-title">Delivery address</h3>

        <div className="form-group">
          <label htmlFor="cg-addr1">Street address</label>
          <input
            type="text"
            id="cg-addr1"
            name="addressLine1"
            value={formData.addressLine1}
            onChange={handleInputChange}
            className={`input-field ${errors.addressLine1 ? 'error' : ''}`}
            placeholder="House / street / area"
            disabled={loading}
            autoComplete="street-address"
          />
          {errors.addressLine1 && (
            <span className="error-message">{errors.addressLine1}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="cg-city">City</label>
          <input
            type="text"
            id="cg-city"
            name="city"
            value={formData.city}
            onChange={handleInputChange}
            className={`input-field ${errors.city ? 'error' : ''}`}
            placeholder="City"
            disabled={loading}
            autoComplete="address-level2"
          />
          {errors.city && <span className="error-message">{errors.city}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="cg-state">State / province (optional)</label>
          <input
            type="text"
            id="cg-state"
            name="state"
            value={formData.state}
            onChange={handleInputChange}
            className="input-field"
            placeholder="State or province"
            disabled={loading}
            autoComplete="address-level1"
          />
        </div>

        <div className="form-group">
          <label htmlFor="cg-postal">Postal code (optional)</label>
          <input
            type="text"
            id="cg-postal"
            name="postalCode"
            value={formData.postalCode}
            onChange={handleInputChange}
            className="input-field"
            placeholder="Postal code"
            disabled={loading}
            autoComplete="postal-code"
          />
        </div>

        <div className="form-group">
          <label htmlFor="cg-country">Country</label>
          <input
            type="text"
            id="cg-country"
            name="country"
            value={formData.country}
            onChange={handleInputChange}
            className="input-field"
            placeholder="Country"
            disabled={loading}
            autoComplete="country-name"
          />
        </div>

        <button
          type="submit"
          className={`auth-submit ${loading ? 'loading' : ''}`}
          disabled={loading}
        >
          {loading ? (
            <div className="loading-spinner">
              <div className="spinner" />
              <span>Creating account…</span>
            </div>
          ) : (
            'Continue to payment'
          )}
        </button>
      </form>
    </div>
  );
};

export default CheckoutGuestAccountForm;
